import React, { useState, useRef, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import styles from './ProjectForm.module.scss';
import { ProjectDocumentationForm, FileItem, FileTag } from '../../../types/projectDocumentation';
import { setShowHeaderSummary } from '../../../store/actions/projectDocumentationActions';
import { openMainModal } from '../../../store/actions/profileActions';
import environment from '../../../environments/index';
import { initiateMercadoPagoOAuth, checkUserMercadoPagoAuth } from '../../../utils/mercadoPagoOAuth.ts';
import { 
  CONSTRUCTION_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  VALIDATION_RULES 
} from '../../../constants/projectDocumentation.ts';
import { 
  getFileConfig, 
  validateFileSize, 
  formatFileSize 
} from '../../../constants/fileConfig.ts';
import { ArrowLeft, UploadIcon } from 'lucide-react';
import TotalSurfaceIcon from '../../../assets/iconsTsx/TotalSurfaceICon.tsx';
import CoveredSurfaceIcon from '../../../assets/iconsTsx/CoveredSurfaceIcon.tsx';
import SemiCoveredSurfaceIcon from '../../../assets/iconsTsx/SemiCoveredSurface.tsx';
import UncoveredSurfaceIcon from '../../../assets/iconsTsx/UncoverSurfaceIcon.tsx';
import EnvironmentIcon from '../../../assets/iconsTsx/EnvironmentIcon.tsx';
import BedRoomIcon from '../../../assets/iconsTsx/BedRoomIcon.tsx';
import BathroomIcon from '../../../assets/iconsTsx/BathroomIcon.tsx';
import FloorIcon from '../../../assets/iconsTsx/FloorIcon.tsx';
import GarageIcon from '../../../assets/iconsTsx/GarageIcon.tsx';
import GardenIcon from '../../../assets/iconsTsx/GardenIcon.tsx';
import TerraceIcon from '../../../assets/iconsTsx/TerraceIcon.tsx';
import PoolIcon from '../../../assets/iconsTsx/PoolIcon.tsx';
import ImplantationIcon from '../../../assets/iconsTsx/ImplantationIcon.tsx';
import ConstructionTypeIcon from '../../../assets/iconsTsx/ConstructionTypeIcon.tsx';
import DollarIcon from '../../../assets/iconsTsx/DollarIcon.tsx';
import ConsultationIcon from '../../../assets/iconsTsx/ConsultationIcon.tsx';
import CheckIcon from '../../../assets/iconsTsx/CheckIcon.tsx';
import CustomSelect from './CustomSelect.tsx';
import FileUploadModal from './FileUploadModal.tsx';
import TermsAndConditionsModal from '../../../components/UI/TermsAndConditionsModal/TermsAndConditionsModal.tsx';
import MercadoPagoAuthModal from '../../../components/UI/MercadoPagoAuthModal/MercadoPagoAuthModal.tsx';
import FilesIcon from '../../../assets/iconsTsx/FileIcon.tsx';
import PdfFileIcon from '../../../assets/iconsTsx/PdfFileIcon.tsx';
import GoogleDriveIcon from '../../../assets/iconsTsx/GoogleDriveIcon.tsx';

const REQUIRED_DOCUMENTS_ANTEPROYECTO = [
  { tagName: 'Carátula', label: 'Carátula', note: 'Imagen o video (se mostrará siempre primero)' },
  { tagName: 'Plano', label: 'Plano', note: null },
  { tagName: 'Vista', label: 'Vista', note: null },
  { tagName: 'Render exterior', label: 'Render exterior', note: null },
  { tagName: 'Memoria descriptiva', label: 'Memoria descriptiva', note: null }
];

const REQUIRED_DOCUMENTS_PROYECTO = [
  { tagName: 'Carátula', label: 'Carátula', note: 'Imagen o video (se mostrará siempre primero)' },
  { tagName: 'Plano', label: 'Plano', note: null },
  { tagName: 'Vista', label: 'Vista', note: null },
  { tagName: 'Render exterior', label: 'Render exterior', note: null },
  { tagName: 'Memoria descriptiva', label: 'Memoria descriptiva', note: null },
  { tagName: 'Cómputo métrico', label: 'Cómputo métrico', note: null },
  { tagName: 'Especificaciones técnicas', label: 'Especificaciones técnicas', note: null }
];

interface ProjectFormProps {
  formData: ProjectDocumentationForm;
  fileTags: FileTag[];
  projectType: 'proyecto' | 'anteproyecto';
  onSubmit: (formData: ProjectDocumentationForm, fileItems: FileItem[]) => void;
  onBack: () => void;
  loading: boolean;
  setShowHeaderSummary: (show: boolean) => void;
  openMainModal: (config: any) => void;
  selectedSubCategory: string | null;
  selectedMaterialities: string[];
  selectedUses: string[];
  hideHeaderText?: boolean;
  initialFileItems?: FileItem[];
  isEditMode?: boolean;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  formData,
  fileTags,
  projectType,
  onSubmit,
  onBack,
  loading,
  setShowHeaderSummary,
  openMainModal,
  selectedSubCategory,
  selectedMaterialities,
  selectedUses,
  initialFileItems,
  hideHeaderText,
  isEditMode = false
}) => {
    const REQUIRED_DOCUMENTS = projectType === 'proyecto' 
      ? REQUIRED_DOCUMENTS_PROYECTO 
      : REQUIRED_DOCUMENTS_ANTEPROYECTO;
    
    const [localFormData, setLocalFormData] = useState<ProjectDocumentationForm>(formData);
    const [fileItems, setFileItems] = useState<FileItem[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showFileUploadModal, setShowFileUploadModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showMercadoPagoModal, setShowMercadoPagoModal] = useState(false);
    const [currentUploadTag, setCurrentUploadTag] = useState<string | null>(null);
    const [uploadTypes, setUploadTypes] = useState<Record<string, 'file' | 'link'>>({});
    const [linkInputs, setLinkInputs] = useState<Record<string, string>>({});
    const [linkErrors, setLinkErrors] = useState<Record<string, string>>({});
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const summaryTriggerRef = useRef<HTMLDivElement>(null);
    const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
    
    const userData = useSelector((state: any) => state.general.userData);

  useEffect(() => {
    if (initialFileItems && initialFileItems.length > 0 && fileItems.length === 0) {
      setFileItems(initialFileItems);
    }
  }, [initialFileItems, fileItems.length]);

  useEffect(() => {
    setLocalFormData(formData);
  }, [formData]);

  // Función para formatear números con separadores de miles
  const formatNumber = (value: number | string): string => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    return numValue.toLocaleString('es-AR', { maximumFractionDigits: 0 });
  };

  // Función para remover formato y obtener número
  const parseFormattedNumber = (value: string): number => {
    const cleanValue = value.replace(/\./g, '').replace(/,/g, '.');
    return parseFloat(cleanValue) || 0;
  };

  // IntersectionObserver para activar el resumen en el HeaderNav
  useEffect(() => {
    if (!summaryTriggerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Notificar a Redux cuando el trigger sale del viewport
        setShowHeaderSummary(!entry.isIntersecting);
      },
      {
        rootMargin: '-80px 0px 0px 0px',
        threshold: 0
      }
    );

    observer.observe(summaryTriggerRef.current);

    return () => {
      observer.disconnect();
      // Limpiar el flag al desmontar
      setShowHeaderSummary(false);
    };
  }, [setShowHeaderSummary]);

  const handleInputChange = (field: keyof ProjectDocumentationForm, value: any) => {
    setLocalFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };

      if (field === 'total_surface' || field === 'covered_surface' || field === 'semicovered_surface' || field === 'uncovered_surface') {
        const totalSurfaces = 
          updatedData.total_surface + 
          updatedData.covered_surface + 
          updatedData.semicovered_surface + 
          updatedData.uncovered_surface;
        
        if (totalSurfaces > 0) {
          setErrors(prev => ({
            ...prev,
            total_surface: '',
            covered_surface: '',
            semicovered_surface: '',
            uncovered_surface: ''
          }));
        }
      }

      return updatedData;
    });
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleUploadDocument = (tagName: string) => {
    const docTag = fileTags.find(tag => {
      const tagNameLower = tag.name.toLowerCase().trim();
      const searchNameLower = tagName.toLowerCase().trim();
      return tagNameLower === searchNameLower || tagNameLower.includes(searchNameLower) || searchNameLower.includes(tagNameLower);
    });
    
    if (!docTag) return;
    
    const fileConfig = getFileConfig(tagName);
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('accept', fileConfig.accept);
    }
    
    setCurrentUploadTag(docTag.id);
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 0);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !currentUploadTag) return;

    const file = files[0];
    
    const currentTag = fileTags.find(tag => tag.id === currentUploadTag);
    const tagName = currentTag?.name || '';
    
    const validation = validateFileSize(file, tagName);
    
    if (!validation.valid) {
      const modalConfig: any = {
        isSuccess: true,
        title: validation.suggestLink ? 'Archivo muy pesado' : 'Archivo no permitido',
        body: validation.error,
        iconType: 'warning',
        noActionNeeded: true
      };
      
      if (validation.suggestLink) {
        modalConfig.body += '\n\nTe recomendamos usar la opción de "Link" para compartir el archivo desde tu Google Drive, Dropbox u otro servicio de almacenamiento.';
      }
      
      openMainModal(modalConfig);
      setCurrentUploadTag(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    const duplicateFile = fileItems.find(
      item => item.type === 'file' && item.name === file.name
    );
    
    if (duplicateFile) {
      openMainModal({
        isSuccess: true,
        title: 'Archivo duplicado',
        body: `El archivo "${file.name}" ya fue agregado. Por favor selecciona un archivo diferente.`,
        iconType: 'warning',
        noActionNeeded: true
      });
      setCurrentUploadTag(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const newFileItem: FileItem = {
      id: `${Date.now()}-${Math.random()}`,
      type: 'file',
      file: file,
      preview: file.type.startsWith('image/') || file.type.startsWith('video/') ? URL.createObjectURL(file) : undefined,
      tagId: currentUploadTag,
      name: file.name
    };

    setFileItems(prev => [...prev, newFileItem]);
    setCurrentUploadTag(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveDocument = (itemId: string) => {
    setFileItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleToggleUploadType = (docTagName: string, type: 'file' | 'link') => {
    setUploadTypes(prev => ({
      ...prev,
      [docTagName]: type
    }));
  };

  const handleLinkInputChange = (docTagName: string, value: string) => {
    setLinkInputs(prev => ({
      ...prev,
      [docTagName]: value
    }));
    
    if (linkErrors[docTagName]) {
      setLinkErrors(prev => ({
        ...prev,
        [docTagName]: ''
      }));
    }
  };

  const handleAddLink = (docTagName: string) => {
    const linkUrl = linkInputs[docTagName]?.trim();
    
    if (!linkUrl) {
      setLinkErrors(prev => ({
        ...prev,
        [docTagName]: 'Por favor ingresa una URL'
      }));
      return;
    }

    try {
      const url = new URL(linkUrl);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        setLinkErrors(prev => ({
          ...prev,
          [docTagName]: 'La URL debe empezar con http:// o https://'
        }));
        return;
      }
    } catch (e) {
      setLinkErrors(prev => ({
        ...prev,
        [docTagName]: 'Por favor ingresa una URL válida (ej: https://drive.google.com/...)'
      }));
      return;
    }

    const duplicateLink = fileItems.find(
      item => item.type === 'link' && item.link === linkUrl
    );
    
    if (duplicateLink) {
      setLinkErrors(prev => ({
        ...prev,
        [docTagName]: 'Este link ya fue agregado'
      }));
      return;
    }

    const docTag = fileTags.find(tag => {
      const tagNameLower = tag.name.toLowerCase().trim();
      const searchNameLower = docTagName.toLowerCase().trim();
      return tagNameLower === searchNameLower || 
             tagNameLower.includes(searchNameLower) || 
             searchNameLower.includes(tagNameLower);
    });

    if (!docTag) return;

    const newLinkItem: FileItem = {
      id: `${Date.now()}-${Math.random()}`,
      type: 'link',
      link: linkUrl,
      tagId: docTag.id,
      name: linkUrl
    };

    setFileItems(prev => [...prev, newLinkItem]);
    
    setLinkInputs(prev => ({
      ...prev,
      [docTagName]: ''
    }));
    setLinkErrors(prev => ({
      ...prev,
      [docTagName]: ''
    }));
  };


  const validateForm = (): { isValid: boolean; firstErrorField?: string } => {
    const newErrors: Record<string, string> = {};
    let firstErrorField: string | undefined;

    if (!localFormData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
      if (!firstErrorField) firstErrorField = 'name';
    } else if (localFormData.name.length < VALIDATION_RULES.name.minLength) {
      newErrors.name = `El nombre debe tener al menos ${VALIDATION_RULES.name.minLength} caracteres`;
      if (!firstErrorField) firstErrorField = 'name';
    }

    if (!localFormData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
      if (!firstErrorField) firstErrorField = 'description';
    } else if (localFormData.description.length < VALIDATION_RULES.description.minLength) {
      newErrors.description = `La descripción debe tener al menos ${VALIDATION_RULES.description.minLength} caracteres`;
      if (!firstErrorField) firstErrorField = 'description';
    }

    const totalSurfaces = 
      localFormData.total_surface + 
      localFormData.covered_surface + 
      localFormData.semicovered_surface + 
      localFormData.uncovered_surface;
    
    if (totalSurfaces === 0) {
      newErrors.total_surface = 'Debe ingresar al menos una superficie mayor a 0';
      newErrors.covered_surface = 'Debe ingresar al menos una superficie mayor a 0';
      newErrors.semicovered_surface = 'Debe ingresar al menos una superficie mayor a 0';
      newErrors.uncovered_surface = 'Debe ingresar al menos una superficie mayor a 0';
      if (!firstErrorField) firstErrorField = 'total_surface';
    }

    if (!localFormData.environments || localFormData.environments <= 0) {
      newErrors.environments = 'Los ambientes son requeridos';
      if (!firstErrorField) firstErrorField = 'environments';
    }

    if (!localFormData.bedrooms || localFormData.bedrooms <= 0) {
      newErrors.bedrooms = 'Los dormitorios son requeridos';
      if (!firstErrorField) firstErrorField = 'bedrooms';
    }

    if (!localFormData.bathrooms || localFormData.bathrooms <= 0) {
      newErrors.bathrooms = 'Los baños son requeridos';
      if (!firstErrorField) firstErrorField = 'bathrooms';
    }

    if (!localFormData.floors || localFormData.floors <= 0) {
      newErrors.floors = 'Los pisos son requeridos';
      if (!firstErrorField) firstErrorField = 'floors';
    }

    if (!localFormData.implantation || localFormData.implantation <= 0) {
      newErrors.implantation = 'La implantación es requerida';
      if (!firstErrorField) firstErrorField = 'implantation';
    } else if (localFormData.implantation > VALIDATION_RULES.implantation.max) {
      newErrors.implantation = `La implantación no puede superar ${VALIDATION_RULES.implantation.max} m²`;
      if (!firstErrorField) firstErrorField = 'implantation';
    }

    if (!localFormData.construction_type) {
      newErrors.construction_type = 'El tipo de construcción es requerido';
      if (!firstErrorField) firstErrorField = 'construction_type';
    }

    if (!localFormData.consultation_price || localFormData.consultation_price <= 0) {
      newErrors.consultation_price = 'El valor hora de consulta es requerido';
      if (!firstErrorField) firstErrorField = 'consultation_price';
    }

    if (!(localFormData as any).consultation_price_currency) {
      newErrors.consultation_price_currency = 'La moneda de consulta es requerida';
      if (!firstErrorField) firstErrorField = 'consultation_price_currency';
    }

    if (!(localFormData as any).price || (localFormData as any).price <= 0) {
      newErrors.price = 'El precio del paquete es requerido';
      if (!firstErrorField) firstErrorField = 'price';
    }

    if (!(localFormData as any).price_currency) {
      newErrors.price_currency = 'La moneda del paquete es requerida';
      if (!firstErrorField) firstErrorField = 'price_currency';
    }

    const itemsWithoutTag = fileItems.filter(item => !item.tagId);
    if (itemsWithoutTag.length > 0) {
      newErrors.files = `Debes asignar una etiqueta a ${itemsWithoutTag.length} archivo(s)`;
      if (!firstErrorField) firstErrorField = 'files';
    }

    const missingRequiredDocs: string[] = [];
    REQUIRED_DOCUMENTS.forEach(doc => {
      const docTag = fileTags.find(tag => {
        const tagNameLower = tag.name.toLowerCase().trim();
        const searchNameLower = doc.tagName.toLowerCase().trim();
        return tagNameLower === searchNameLower || 
               tagNameLower.includes(searchNameLower) || 
               searchNameLower.includes(tagNameLower);
      });
      
      if (docTag) {
        const hasFile = fileItems.some(item => String(item.tagId) === String(docTag.id));
        if (!hasFile) {
          missingRequiredDocs.push(doc.label);
        }
      }
    });

    if (missingRequiredDocs.length > 0) {
      newErrors.requiredDocs = `Faltan documentos requeridos: ${missingRequiredDocs.join(', ')}`;
      if (!firstErrorField) firstErrorField = 'requiredDocs';
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, firstErrorField };
  };

  const isFormComplete = (): boolean => {
    // Campos básicos
    if (!localFormData.name.trim() || localFormData.name.length < VALIDATION_RULES.name.minLength) return false;
    if (!localFormData.description.trim() || localFormData.description.length < VALIDATION_RULES.description.minLength) return false;

    // Superficies
    const totalSurfaces = 
      localFormData.total_surface + 
      localFormData.covered_surface + 
      localFormData.semicovered_surface + 
      localFormData.uncovered_surface;
    if (totalSurfaces === 0) return false;

    // Ambientes, dormitorios, baños y pisos
    if (!localFormData.environments || localFormData.environments <= 0) return false;
    if (!localFormData.bedrooms || localFormData.bedrooms <= 0) return false;
    if (!localFormData.bathrooms || localFormData.bathrooms <= 0) return false;
    if (!localFormData.floors || localFormData.floors <= 0) return false;

    // Implantación y tipo de construcción
    if (!localFormData.implantation || localFormData.implantation <= 0) return false;
    if (!localFormData.construction_type) return false;

    // Valor hora consulta y moneda
    if (!localFormData.consultation_price || localFormData.consultation_price <= 0) return false;
    if (!(localFormData as any).consultation_price_currency) return false;

    // Precio del paquete y moneda del paquete
    if (!(localFormData as any).price || (localFormData as any).price <= 0) return false;
    if (!(localFormData as any).price_currency) return false;

    // Documentos requeridos
    const allRequiredDocsUploaded = REQUIRED_DOCUMENTS.every(doc => {
      const docTag = fileTags.find(tag => {
        const tagNameLower = tag.name.toLowerCase().trim();
        const searchNameLower = doc.tagName.toLowerCase().trim();
        return tagNameLower === searchNameLower || 
               tagNameLower.includes(searchNameLower) || 
               searchNameLower.includes(tagNameLower);
      });
      
      if (!docTag) return false;
      return fileItems.some(item => String(item.tagId) === String(docTag.id));
    });

    if (!allRequiredDocsUploaded) return false;

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    setHasAttemptedSubmit(true);
    
    const { isValid, firstErrorField } = validateForm();
    
    if (!isValid && firstErrorField) {
      setTimeout(() => {
        const element = fieldRefs.current[firstErrorField];
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const targetPosition = absoluteElementTop - (window.innerHeight / 2);
          
          const startPosition = window.pageYOffset;
          const distance = targetPosition - startPosition;
          const duration = 800;
          let start = null;

          const animation = (currentTime) => {
            if (start === null) start = currentTime;
            const timeElapsed = currentTime - start;
            const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);
            window.scrollTo(0, run);
            if (timeElapsed < duration) requestAnimationFrame(animation);
          };

          const easeInOutQuad = (t, b, c, d) => {
            t /= d / 2;
            if (t < 1) return c / 2 * t * t + b;
            t--;
            return -c / 2 * (t * (t - 2) - 1) + b;
          };

          requestAnimationFrame(animation);
          
          if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            element.focus();
          }
        }
      }, 100);
    } else if (isValid) {
      if (isEditMode) {
        // En modo edición, no mostrar modal de términos, llamar directamente a onSubmit
        onSubmit(localFormData, fileItems);
      } else {
        // En modo creación, mostrar modal de términos
        setShowTermsModal(true);
      }
    }
  };

  const handleAcceptTerms = () => {
    setShowTermsModal(false);
    setShowMercadoPagoModal(true);
  };

  const handleAcceptMercadoPago = async () => {
    setShowMercadoPagoModal(false);
    
    const hasMerchantAccount = checkUserMercadoPagoAuth(userData);
    
    if (hasMerchantAccount) {
      try {
        await onSubmit(localFormData, fileItems);
      } catch (error) {
        console.error('Error al crear proyecto:', error);
      }
    } else {
      try {
        await initiateMercadoPagoOAuth(
          {
            clientId: environment.mercadoPago.clientId,
            redirectUri: environment.mercadoPago.redirectUri
          },
          localFormData,
          fileItems,
          projectType,
          selectedSubCategory || '',
          selectedMaterialities,
          selectedUses
        );
      } catch (err) {
        openMainModal({
          isSuccess: false,
          title: 'No pudimos preparar los archivos',
          body: 'Ocurrió un problema al preparar tus archivos antes de redirigir a Mercado Pago. Te recomendamos reintentar o adjuntar los archivos como links. Si el problema persiste, intentá desde una PC.',
          iconType: 'error',
          noActionNeeded: true
        });
      }
    }
  };

  const handleCancelMercadoPago = () => {
    setShowMercadoPagoModal(false);
  };

  const handleCancelTerms = () => {
    setShowTermsModal(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {!hideHeaderText && (
          <>
          <div className={styles.headerText}>
            <h2 className={styles.title}>Información del {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'}</h2>
            <p className={styles.description}>
              Completa los detalles finales de tu {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'}
            </p>
          </div>
        
        <button className={styles.backButton} onClick={onBack} type="button">
          <ArrowLeft size={16} />
          Volver
        </button>
        </>
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Input oculto para selección de archivos individuales */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        {/* Trigger invisible para IntersectionObserver */}
        <div ref={summaryTriggerRef} style={{ height: '1px', width: '100%' }} />

        {/* Información Básica */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Información Básica</h3>
          
          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.label}>
              Nombre del {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'} *
            </label>
            <input
              ref={(el) => { if (el) fieldRefs.current['name'] = el; }}
              type="text"
              id="name"
              value={localFormData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`${styles.input} ${errors.name ? styles.error : ''}`}
              placeholder="Ej: Casa moderna de 3 dormitorios"
            />
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>
              Descripción *
            </label>
            <textarea
              ref={(el) => { if (el) fieldRefs.current['description'] = el; }}
              id="description"
              value={localFormData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={`${styles.textarea} ${errors.description ? styles.error : ''}`}
              placeholder={`Describe detalladamente tu ${projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'}...`}
              rows={4}
            />
            <div className={styles.characterCount}>
              {localFormData.description.length}/{VALIDATION_RULES.description.minLength} caracteres mínimos
            </div>
            {errors.description && <span className={styles.errorText}>{errors.description}</span>}
          </div>
        </div>

        {/* Superficies */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Superficies (m²) *</h3>
          <div className={styles.gridTwo}>
            <div className={styles.formGroup}>
              <label htmlFor="total_surface" className={styles.labelWithIcon}>
                <TotalSurfaceIcon className={styles.icon} />
                Superficie Total
              </label>
              <div className={`${styles.inputWithSuffix} ${errors.total_surface ? styles.error : ''}`}>
                <input
                  ref={(el) => { if (el) fieldRefs.current['total_surface'] = el; }}
                  type="text"
                  id="total_surface"
                  value={formatNumber(localFormData.total_surface)}
                  onChange={(e) => handleInputChange('total_surface', parseFormattedNumber(e.target.value))}
                  className={styles.input}
                  placeholder="0"
                />
                <span className={styles.inputSuffix}>m²</span>
              </div>
              {errors.total_surface && <span className={styles.errorText}>{errors.total_surface}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="covered_surface" className={styles.labelWithIcon}>
                <CoveredSurfaceIcon className={styles.icon} />
                Superficie Cubierta
              </label>
              <div className={`${styles.inputWithSuffix} ${errors.covered_surface ? styles.error : ''}`}>
                <input
                  type="text"
                  id="covered_surface"
                  value={formatNumber(localFormData.covered_surface)}
                  onChange={(e) => handleInputChange('covered_surface', parseFormattedNumber(e.target.value))}
                  className={styles.input}
                  placeholder="0"
                />
                <span className={styles.inputSuffix}>m²</span>
              </div>
              {errors.covered_surface && <span className={styles.errorText}>{errors.covered_surface}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="semicovered_surface" className={styles.labelWithIcon}>
                <SemiCoveredSurfaceIcon className={styles.icon} />
                Superficie Semicubierta
              </label>
              <div className={`${styles.inputWithSuffix} ${errors.semicovered_surface ? styles.error : ''}`}>
                <input
                  type="text"
                  id="semicovered_surface"
                  value={formatNumber(localFormData.semicovered_surface)}
                  onChange={(e) => handleInputChange('semicovered_surface', parseFormattedNumber(e.target.value))}
                  className={styles.input}
                  placeholder="0"
                />
                <span className={styles.inputSuffix}>m²</span>
              </div>
              {errors.semicovered_surface && <span className={styles.errorText}>{errors.semicovered_surface}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="uncovered_surface" className={styles.labelWithIcon}>
                <UncoveredSurfaceIcon className={styles.icon} />
                Superficie Descubierta
              </label>
              <div className={`${styles.inputWithSuffix} ${errors.uncovered_surface ? styles.error : ''}`}>
                <input
                  type="text"
                  id="uncovered_surface"
                  value={formatNumber(localFormData.uncovered_surface)}
                  onChange={(e) => handleInputChange('uncovered_surface', parseFormattedNumber(e.target.value))}
                  className={styles.input}
                  placeholder="0"
                />
                <span className={styles.inputSuffix}>m²</span>
              </div>
              {errors.uncovered_surface && <span className={styles.errorText}>{errors.uncovered_surface}</span>}
            </div>
          </div>
        </div>

        {/* Ambientes */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Ambientes</h3>
          <div className={styles.gridFour}>
            <div className={styles.formGroup}>
              <label htmlFor="environments" className={styles.labelWithIcon}>
                <EnvironmentIcon className={styles.icon} />
                Ambientes *
              </label>
              <input
                ref={(el) => { if (el) fieldRefs.current['environments'] = el; }}
                type="number"
                id="environments"
                value={localFormData.environments || ''}
                onChange={(e) => handleInputChange('environments', parseInt(e.target.value) || 0)}
                className={`${styles.input} ${errors.environments ? styles.error : ''}`}
                placeholder="0"
                min="0"
              />
              {errors.environments && <span className={styles.errorText}>{errors.environments}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="bedrooms" className={styles.labelWithIcon}>
                <BedRoomIcon className={styles.icon} />
                Dormitorios *
              </label>
              <input
                ref={(el) => { if (el) fieldRefs.current['bedrooms'] = el; }}
                type="number"
                id="bedrooms"
                value={localFormData.bedrooms || ''}
                onChange={(e) => handleInputChange('bedrooms', parseInt(e.target.value) || 0)}
                className={`${styles.input} ${errors.bedrooms ? styles.error : ''}`}
                placeholder="0"
                min="0"
              />
              {errors.bedrooms && <span className={styles.errorText}>{errors.bedrooms}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="bathrooms" className={styles.labelWithIcon}>
                <BathroomIcon className={styles.icon} />
                Baños *
              </label>
              <input
                ref={(el) => { if (el) fieldRefs.current['bathrooms'] = el; }}
                type="number"
                id="bathrooms"
                value={localFormData.bathrooms || ''}
                onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 0)}
                className={`${styles.input} ${errors.bathrooms ? styles.error : ''}`}
                placeholder="0"
                min="0"
              />
              {errors.bathrooms && <span className={styles.errorText}>{errors.bathrooms}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="floors" className={styles.labelWithIcon}>
                <FloorIcon className={styles.icon} />
                Pisos *
              </label>
              <input
                ref={(el) => { if (el) fieldRefs.current['floors'] = el; }}
                type="number"
                id="floors"
                value={localFormData.floors || ''}
                onChange={(e) => handleInputChange('floors', parseInt(e.target.value) || 0)}
                className={`${styles.input} ${errors.floors ? styles.error : ''}`}
                placeholder="0"
                min="0"
              />
              {errors.floors && <span className={styles.errorText}>{errors.floors}</span>}
            </div>
          </div>
        </div>

        {/* Características */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Características</h3>
          <div className={styles.gridFour}>
            <div
              className={`${styles.characteristicCard} ${localFormData.garage ? styles.selected : ''}`}
              onClick={() => handleInputChange('garage', !localFormData.garage)}
            >
              <GarageIcon className={styles.characteristicIcon} />
              <span className={styles.characteristicName}>Cochera</span>
              {localFormData.garage && (
                <div className={styles.checkmark}>
                  <CheckIcon className={styles.checkIcon} />
                </div>
              )}
            </div>

            <div
              className={`${styles.characteristicCard} ${localFormData.yard ? styles.selected : ''}`}
              onClick={() => handleInputChange('yard', !localFormData.yard)}
            >
              <GardenIcon className={styles.characteristicIcon} />
              <span className={styles.characteristicName}>Patio</span>
              {localFormData.yard && (
                <div className={styles.checkmark}>
                  <CheckIcon className={styles.checkIcon} />
                </div>
              )}
            </div>

            <div
              className={`${styles.characteristicCard} ${localFormData.terrace ? styles.selected : ''}`}
              onClick={() => handleInputChange('terrace', !localFormData.terrace)}
            >
              <TerraceIcon className={styles.characteristicIcon} />
              <span className={styles.characteristicName}>Terraza</span>
              {localFormData.terrace && (
                <div className={styles.checkmark}>
                  <CheckIcon className={styles.checkIcon} />
                </div>
              )}
            </div>

            <div
              className={`${styles.characteristicCard} ${localFormData.pool ? styles.selected : ''}`}
              onClick={() => handleInputChange('pool', !localFormData.pool)}
            >
              <PoolIcon className={styles.characteristicIcon} />
              <span className={styles.characteristicName}>Piscina</span>
              {localFormData.pool && (
                <div className={styles.checkmark}>
                  <CheckIcon className={styles.checkIcon} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Detalles Técnicos */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Detalles Técnicos</h3>
          <div className={styles.gridTwo}>
            <div className={styles.formGroup}>
              <label htmlFor="implantation" className={styles.labelWithIcon}>
                <ImplantationIcon className={styles.icon} />
                Pisada (m²) *
              </label>
              <input
                ref={(el) => { if (el) fieldRefs.current['implantation'] = el; }}
                type="number"
                id="implantation"
                value={localFormData.implantation || ''}
                onChange={(e) => handleInputChange('implantation', parseFloat(e.target.value) || 0)}
                className={`${styles.input} ${errors.implantation ? styles.error : ''}`}
                placeholder="0"
                min="0"
                step="1"
              />
              {errors.implantation && <span className={styles.errorText}>{errors.implantation}</span>}
            </div>

            <div ref={(el) => { if (el) fieldRefs.current['construction_type'] = el; }}>
              <CustomSelect
                value={localFormData.construction_type}
                onChange={(value) => handleInputChange('construction_type', value)}
                options={CONSTRUCTION_TYPE_OPTIONS}
                icon={<ConstructionTypeIcon className={styles.icon} />}
                label="Tipo de Construcción *"
                error={errors.construction_type}
              />
            </div>
          </div>
        </div>

        {/* Precios */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Precios</h3>
          <div className={styles.gridTwo}>
            <div className={styles.formGroup}>
              <label htmlFor="budget" className={styles.labelWithIcon}>
                <DollarIcon className={styles.icon} />
                Presupuesto estimado
              </label>
              <div className={styles.inputWithUnit}>
                <span className={styles.inputPrefix}>$</span>
                <input
                  type="text"
                  id="budget"
                  value={formatNumber(localFormData.budget)}
                  onChange={(e) => handleInputChange('budget', parseFormattedNumber(e.target.value))}
                  className={`${styles.input} ${styles.inputWithPrefix}`}
                  placeholder="0"
                />
              </div>
            </div>

            <CustomSelect
              value={(localFormData as any).budget_currency || 'ARS'}
              onChange={(value) => handleInputChange('budget_currency' as any, value)}
              options={CURRENCY_OPTIONS}
              icon={<DollarIcon className={styles.icon} />}
              label="Moneda del presupuesto"
              error={errors.budget_currency}
            />
          </div>

          <div className={styles.gridTwo}>
            <div className={styles.formGroup}>
              <label htmlFor="consultation_price" className={styles.labelWithIcon}>
                <ConsultationIcon className={styles.icon} />
                Valor hora de consulta *
              </label>
              <div className={styles.inputWithUnit}>
                <span className={styles.inputPrefix}>$</span>
                <input
                  ref={(el) => { if (el) fieldRefs.current['consultation_price'] = el; }}
                  type="text"
                  id="consultation_price"
                  value={formatNumber(localFormData.consultation_price)}
                  onChange={(e) => handleInputChange('consultation_price', parseFormattedNumber(e.target.value))}
                  className={`${styles.input} ${styles.inputWithPrefix} ${errors.consultation_price ? styles.error : ''}`}
                  placeholder="0"
                />
              </div>
              <div className={styles.fieldDescription}>
                Se mostrará a los clientes para consultas sobre este {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'}
              </div>
              {errors.consultation_price && <span className={styles.errorText}>{errors.consultation_price}</span>}
            </div>

            <div ref={(el) => { if (el) fieldRefs.current['consultation_price_currency'] = el; }}>
              <CustomSelect
                value={(localFormData as any).consultation_price_currency || 'ARS'}
                onChange={(value) => handleInputChange('consultation_price_currency' as any, value)}
                options={CURRENCY_OPTIONS}
                icon={<DollarIcon className={styles.icon} />}
                label="Moneda de consulta *"
                error={errors.consultation_price_currency}
              />
            </div>
          </div>

          <div className={styles.gridTwo}>
            <div className={styles.formGroup}>
              <label htmlFor="price" className={styles.labelWithIcon}>
                <DollarIcon className={styles.icon} />
                Precio de venta del {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'} *
              </label>
              <div className={styles.inputWithUnit}>
                <span className={styles.inputPrefix}>$</span>
                <input
                  ref={(el) => { if (el) fieldRefs.current['price'] = el; }}
                  type="text"
                  id="price"
                  value={formatNumber((localFormData as any).price)}
                  onChange={(e) => handleInputChange('price' as any, parseFormattedNumber(e.target.value))}
                  className={`${styles.input} ${styles.inputWithPrefix} ${errors.price ? styles.error : ''}`}
                  placeholder="0"
                />
              </div>
              <div className={styles.fieldDescription}>
                Precio al que quieres vender este {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'} en el marketplace
              </div>
              {errors.price && <span className={styles.errorText}>{errors.price}</span>}
            </div>

            <div ref={(el) => { if (el) fieldRefs.current['price_currency'] = el; }}>
              <CustomSelect
                value={(localFormData as any).price_currency || 'ARS'}
                onChange={(value) => handleInputChange('price_currency' as any, value)}
                options={CURRENCY_OPTIONS}
                icon={<DollarIcon className={styles.icon} />}
                label="Moneda de venta *"
                error={errors.price_currency}
              />
              {(localFormData as any).price_currency === 'USD' && (
                <div className={styles.fieldDescription}>
                  <strong>Importante:</strong> El cobro se realizará en pesos argentinos al tipo de cambio oficial 
                  informado por el Banco Nación al momento del pago.
                </div>
              )}
            </div>
          </div>
        </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Documentación</h3>
              
              <div className={styles.documentationGrid}>
                <div className={styles.requiredSection}>
                  <h4 className={styles.requiredTitle}>Documentos Requeridos</h4>
                  <div className={styles.requiredDocsList}>
                    {REQUIRED_DOCUMENTS.map((doc) => {
                      const docTag = fileTags.find(tag => {
                        const tagNameLower = tag.name.toLowerCase().trim();
                        const searchNameLower = doc.tagName.toLowerCase().trim();
                        return tagNameLower === searchNameLower || 
                               tagNameLower.includes(searchNameLower) || 
                               searchNameLower.includes(tagNameLower);
                      });
                      
                      const uploadedFiles = fileItems.filter(item => {
                        if (!docTag) return false;
                        return String(item.tagId) === String(docTag.id);
                      });
                      
                      const currentUploadType = uploadTypes[doc.tagName] || 'file';
                      const fileConfig = getFileConfig(doc.tagName);
                      
                      return (
                        <div key={doc.tagName} className={styles.docUploadCard}>
                          <div className={styles.docInfo}>
                            <span className={styles.docName}>{doc.label}</span>
                            {doc.note ? (
                              <span className={styles.docNote}>{doc.note}</span>
                            ) : (
                              <span className={styles.docNote}>Tipos: {fileConfig.description}</span>
                            )}
                          </div>
                          
                          {uploadedFiles.length > 0 ? (
                            <div className={styles.uploadedFilesContainer}>
                              {uploadedFiles.map((uploadedFile) => (
                                <div key={uploadedFile.id} className={styles.uploadedPreview}>
                                  {uploadedFile.type === 'link' ? (
                                    <GoogleDriveIcon className={styles.linkIcon} />
                                  ) : uploadedFile.type === 'file' && uploadedFile.file?.type.startsWith('video/') ? (
                                    <video src={uploadedFile.preview} className={styles.previewThumb} />
                                  ) : uploadedFile.type === 'file' && uploadedFile.preview ? (
                                    <img src={uploadedFile.preview} alt={uploadedFile.name} className={styles.previewThumb} />
                                  ) : uploadedFile.type === 'file' && uploadedFile.file?.type === 'application/pdf' ? (
                                    <PdfFileIcon className={styles.fileThumbIcon} />
                                  ) : (
                                    <FilesIcon className={styles.fileThumbIcon} />
                                  )}
                                  <span className={styles.fileName}>
                                    {uploadedFile.type === 'link' 
                                      ? uploadedFile.link 
                                      : uploadedFile.name}
                                  </span>
                                  <span className={styles.fileSize}>
                                    {uploadedFile.type === 'file' && uploadedFile.file 
                                      ? formatFileSize(uploadedFile.file.size)
                                      : ''}
                                  </span>
                                  <button
                                    type="button"
                                    className={styles.removeDocButton}
                                    onClick={() => handleRemoveDocument(uploadedFile.id)}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                              <div className={styles.uploadControl}>
                                <div className={styles.toggleButtons}>
                                  <button
                                    type="button"
                                    className={`${styles.toggleButton} ${currentUploadType === 'file' ? styles.active : ''}`}
                                    onClick={() => handleToggleUploadType(doc.tagName, 'file')}
                                  >
                                    Archivo
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.toggleButton} ${currentUploadType === 'link' ? styles.active : ''}`}
                                    onClick={() => handleToggleUploadType(doc.tagName, 'link')}
                                  >
                                    Link
                                  </button>
                                </div>
                                
                                {currentUploadType === 'file' ? (
                                  <button
                                    type="button"
                                    className={styles.uploadDocButton}
                                    onClick={() => handleUploadDocument(doc.tagName)}
                                    disabled={loading}
                                  >
                                    <UploadIcon className={styles.icon} />
                                    Agregar más
                                  </button>
                                ) : (
                                  <div className={styles.linkInputWrapper}>
                                    <div className={styles.linkInputContainer}>
                                      <input
                                        type="url"
                                        placeholder="https://drive.google.com/..."
                                        value={linkInputs[doc.tagName] || ''}
                                        onChange={(e) => handleLinkInputChange(doc.tagName, e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddLink(doc.tagName);
                                          }
                                        }}
                                        className={`${styles.linkInput} ${linkErrors[doc.tagName] ? styles.linkInputError : ''}`}
                                        disabled={loading}
                                      />
                                      <button
                                        type="button"
                                        className={styles.addLinkButton}
                                        onClick={() => handleAddLink(doc.tagName)}
                                        disabled={loading || !linkInputs[doc.tagName]?.trim()}
                                      >
                                        Agregar
                                      </button>
                                    </div>
                                    {linkErrors[doc.tagName] && (
                                      <span className={styles.linkErrorText}>
                                        {linkErrors[doc.tagName]}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className={styles.uploadControl}>
                              <div className={styles.toggleButtons}>
                                <button
                                  type="button"
                                  className={`${styles.toggleButton} ${currentUploadType === 'file' ? styles.active : ''}`}
                                  onClick={() => handleToggleUploadType(doc.tagName, 'file')}
                                >
                                  Archivo
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.toggleButton} ${currentUploadType === 'link' ? styles.active : ''}`}
                                  onClick={() => handleToggleUploadType(doc.tagName, 'link')}
                                >
                                  Link
                                </button>
                              </div>
                              
                              {currentUploadType === 'file' ? (
                                <button
                                  type="button"
                                  className={styles.uploadDocButton}
                                  onClick={() => handleUploadDocument(doc.tagName)}
                                  disabled={loading}
                                >
                                  <UploadIcon className={styles.icon} />
                                  Seleccionar archivo
                                </button>
                              ) : (
                                <div className={styles.linkInputWrapper}>
                                  <div className={styles.linkInputContainer}>
                                    <input
                                      type="url"
                                      placeholder="https://drive.google.com/..."
                                      value={linkInputs[doc.tagName] || ''}
                                      onChange={(e) => handleLinkInputChange(doc.tagName, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleAddLink(doc.tagName);
                                        }
                                      }}
                                      className={`${styles.linkInput} ${linkErrors[doc.tagName] ? styles.linkInputError : ''}`}
                                      disabled={loading}
                                    />
                                    <button
                                      type="button"
                                      className={styles.addLinkButton}
                                      onClick={() => handleAddLink(doc.tagName)}
                                      disabled={loading || !linkInputs[doc.tagName]?.trim()}
                                    >
                                      Agregar
                                    </button>
                                  </div>
                                  {linkErrors[doc.tagName] && (
                                    <span className={styles.linkErrorText}>
                                      {linkErrors[doc.tagName]}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.optionalSection}>
                  <h4 className={styles.optionalTitle}>Documentación Adicional (Opcional)</h4>
                  <p className={styles.optionalDescription}>
                    Agrega cualquier documento extra que consideres relevante para tu proyecto.
                  </p>
                  
                  <button 
                    type="button"
                    className={styles.addOptionalButton}
                    onClick={() => setShowFileUploadModal(true)}
                    disabled={loading}
                  >
                    <FilesIcon className={styles.icon} />
                    Agregar Más Documentos
                  </button>

                  {fileItems.filter(item => {
                    const isRequired = REQUIRED_DOCUMENTS.some(doc => {
                      const docTag = fileTags.find(tag => {
                        const tagNameLower = tag.name.toLowerCase().trim();
                        const searchNameLower = doc.tagName.toLowerCase().trim();
                        return tagNameLower === searchNameLower || 
                               tagNameLower.includes(searchNameLower) || 
                               searchNameLower.includes(tagNameLower);
                      });
                      if (!docTag) return false;
                      return String(item.tagId) === String(docTag.id);
                    });
                    return !isRequired;
                  }).length > 0 && (
                    <div className={styles.optionalFilesList}>
                      {fileItems.filter(item => {
                        const isRequired = REQUIRED_DOCUMENTS.some(doc => {
                          const docTag = fileTags.find(tag => {
                            const tagNameLower = tag.name.toLowerCase().trim();
                            const searchNameLower = doc.tagName.toLowerCase().trim();
                            return tagNameLower === searchNameLower || 
                                   tagNameLower.includes(searchNameLower) || 
                                   searchNameLower.includes(tagNameLower);
                          });
                          return docTag && String(item.tagId) === String(docTag.id);
                        });
                        return !isRequired;
                      }).map(item => {
                        const itemTag = fileTags.find(t => t.id === item.tagId);
                        return (
                          <div key={item.id} className={styles.optionalFileItem}>
                            {item.type === 'link' ? (
                              <GoogleDriveIcon className={styles.optionalFileIcon} />
                            ) : item.file?.type.startsWith('video/') ? (
                              <video src={item.preview} className={styles.optionalFileThumb} />
                            ) : item.preview ? (
                              <img src={item.preview} alt={item.name} className={styles.optionalFileThumb} />
                            ) : item.file?.type === 'application/pdf' ? (
                              <PdfFileIcon className={styles.optionalFileIcon} />
                            ) : (
                              <FilesIcon className={styles.optionalFileIcon} />
                            )}
                            <div className={styles.optionalFileInfo}>
                              <span className={styles.optionalFileName}>
                                {item.type === 'link' ? item.link : item.name}
                              </span>
                              <div className={styles.optionalFileMetadata}>
                                <span className={styles.optionalFileTag}>{itemTag?.name}</span>
                                {item.type === 'file' && item.file && (
                                  <span className={styles.optionalFileSize}>
                                    {formatFileSize(item.file.size)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className={styles.removeOptionalButton}
                              onClick={() => handleRemoveDocument(item.id)}
                              title="Eliminar documento"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              {errors.files && <span ref={(el) => { if (el) fieldRefs.current['files'] = el; }} className={styles.errorText}>{errors.files}</span>}
              {errors.requiredDocs && <span ref={(el) => { if (el) fieldRefs.current['requiredDocs'] = el; }} className={styles.errorText}>{errors.requiredDocs}</span>}
            </div>

        <div className={styles.footer}>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={
              loading || 
              (hasAttemptedSubmit && !isFormComplete()) || 
              (isEditMode && (!selectedSubCategory || selectedSubCategory === '' || selectedSubCategory === '0'))
            }
          >
            {loading ? 'Guardando...' : isEditMode ? `Actualizar ${projectType === 'proyecto' ? 'Proyecto' : 'Ante Proyecto'}` : `Crear ${projectType === 'proyecto' ? 'Proyecto' : 'Ante Proyecto'}`}
          </button>
        </div>
      </form>

      {/* Modal de gestión de archivos */}
      <FileUploadModal
        isOpen={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        items={fileItems}
        tags={fileTags}
        onItemsChange={setFileItems}
        loading={loading}
      />

      {/* Modal de términos y condiciones */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onAccept={handleAcceptTerms}
        onCancel={handleCancelTerms}
        projectType={projectType}
      />

      {/* Modal de autorización de Mercado Pago */}
      <MercadoPagoAuthModal
        isOpen={showMercadoPagoModal}
        onAccept={handleAcceptMercadoPago}
        onCancel={handleCancelMercadoPago}
        projectType={projectType}
        consultationPrice={(localFormData as any).price || 0}
        currency={(localFormData as any).price_currency || 'ARS'}
        hasLinkedAccount={checkUserMercadoPagoAuth(userData)}
      />
    </div>
  );
};

export default connect(null, {
  setShowHeaderSummary,
  openMainModal
})(ProjectForm);
